import {getLoginAuthorizationParams} from './login-params.utils';

import type {LoginContext} from './login-params.utils';

const REAUTH_REQUIRED_ERROR_CODES = new Set([
  'invalid_grant',
  'login_required',
  'missing_refresh_token'
]);

function isReauthRequiredError(error: unknown): boolean {
  const code = (error as {error?: unknown} | null)?.error;
  return typeof code === 'string' && REAUTH_REQUIRED_ERROR_CODES.has(code);
}

export interface TokenRecoveryDeps {
  getAccessTokenSilently: () => Promise<string>;
  loginWithRedirect: (options?: {
    authorizationParams?: ReturnType<typeof getLoginAuthorizationParams>;
    appState?: {returnTo?: string};
  }) => Promise<void>;
  /** Injected rather than read from `window` here so this module stays DOM-free and unit-testable. */
  getReturnToPath: () => string;
  getUiLocale: () => string;
  getTheme?: () => string | undefined;
  getLoginContext?: () => LoginContext;
}

/**
 * Module-level (not per-getter) so every `createRecoveringTokenGetter` instance across the app
 * — one per `useApiClient` caller, plus the SSE hook — shares a single in-flight redirect
 * instead of each racing its own.
 */
let redirectPromise: Promise<void> | null = null;

/**
 * Auth0's refresh token can go dead mid-session (rotation reuse detection, absolute/idle
 * expiry, revocation) — `getAccessTokenSilently` then rejects instead of recovering, so every
 * call site would otherwise need its own logout/login handling. Wrapping it here redirects
 * through Auth0 once per dead-token episode; the SSO cookie usually makes that redirect resolve
 * silently, so the caller's request still fails this one time but the next one succeeds.
 */
export function createRecoveringTokenGetter({
  getAccessTokenSilently,
  loginWithRedirect,
  getReturnToPath,
  getUiLocale,
  getTheme,
  getLoginContext
}: TokenRecoveryDeps): () => Promise<string> {
  return async function getTokenOrReauth(): Promise<string> {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      if (isReauthRequiredError(error)) {
        redirectPromise ??= loginWithRedirect({
          authorizationParams: getLoginAuthorizationParams({
            locale: getUiLocale(),
            theme: getTheme?.(),
            context: getLoginContext?.()
          }),
          appState: {returnTo: getReturnToPath()}
        }).finally(() => {
          redirectPromise = null;
        });
        await redirectPromise.catch(() => {});
      }
      throw error;
    }
  };
}
